/**
 * Protocol freeze contract (F12, milestone M3): clarifications, protocol
 * settings, the frozen protocol document, and the evidence page created from
 * a frozen question. Re-exported from index.ts.
 *
 * Owner: protocol freeze builder (F12). Shapes follow
 * specs/features/F12-question-builder/design.md, Interfaces.
 *
 * The *ProtocolFields interfaces are merged into Question, SaveQuestionRequest
 * and PicoDraftResponse in index.ts. The Worker and the web app changed together
 * when they became required (F12 design, Risks: contract change).
 */
import type { EligibilityRule, OutcomeDefinition, Pico, Protocol, QuestionStudyDesign, ReviewType, StudyDesign, TsaProtocol } from '@bioterafi/evidence-model';
import { questionPath } from './paths';

export const PROTOCOL_ROUTES = {
  /** POST FreezeProtocolRequest -> 200 Question with status 'protocol_frozen' (E21; QB-080). DELETE of a draft is ROUTES.question(id) (E20; QB-067). */
  questionFreeze: (id: string) => `${questionPath(id)}/freeze`,
  /** GET -> 200 text/markdown, the PROSPERO-style protocol document (E70; QB-100). */
  questionProtocolDocument: (id: string) => `${questionPath(id)}/protocol.md`,
  /** POST -> 201 CreateEvidencePageFromQuestionResponse (E71; QB-110). */
  questionEvidencePage: (id: string) => `${questionPath(id)}/evidence-page`,
  /** POST -> 201 Question, a new draft copied from this one (E72; QB-092). */
  questionDuplicate: (id: string) => `${questionPath(id)}/duplicate`,
} as const;

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type ClarificationStatus = 'open' | 'resolved' | 'dismissed';

/** QB-052, QB-053. */
export interface Clarification {
  /** 1 to 500 characters. */
  text: string;
  status: ClarificationStatus;
  /** 0 to 1 000 characters; not empty when dismissed. */
  note: string;
}

/** ADR-0014; QB-075. */
export type SubgroupTest = 'separate_tau2' | 'common_tau2';

/** QB-073: the docs/spec.md §6.6 dimensions; `rob` is filled from the RoB 2 overall judgement. */
export const DEFAULT_SUBGROUP_DIMENSIONS: readonly string[] = Object.freeze(['ageBand', 'dose', 'setting', 'incomeLevel', 'rob']);
/** QB-075, ADR-0014. */
export const DEFAULT_SUBGROUP_TEST: SubgroupTest = 'separate_tau2';
/** QB-076: DEFAULT_TSA_PARAMETERS of @bioterafi/stats, with null meaning the pooled control-arm risk. */
export const DEFAULT_TSA_PROTOCOL: Readonly<TsaProtocol> = Object.freeze({
  alpha: 0.05,
  beta: 0.1,
  relativeRiskReduction: 0.2,
  controlEventProportion: null,
  heterogeneityAdjustment: 'D2',
});

/** Limits shared by the Worker's validator and the builder (QB-052, QB-072 to QB-074). */
export const PROTOCOL_LIMITS = Object.freeze({
  clarifications: 10,
  clarificationText: 500,
  clarificationNote: 1000,
  subgroupDimensions: 10,
  metaRegressionCovariates: 5,
  rationale: 1000,
  /** Confidence levels a protocol may pre-specify (QB-071). */
  levels: Object.freeze([0.9, 0.95, 0.99]),
});

/** Subgroup dimension and covariate keys (QB-073, QB-074). */
export const SETTINGS_KEY_PATTERN = /^[a-z][A-Za-z0-9]{0,39}$/;

/** The engine's AnalysisOptions (F01), as carried by the evidence-model Protocol. */
export type AnalysisSettings = Protocol['analysis'];

/** QB-070 to QB-076. */
export interface ProtocolSettings {
  /** Default DEFAULT_ANALYSIS_OPTIONS (QB-070). */
  analysis: AnalysisSettings;
  /** Default ['ageBand', 'dose', 'setting', 'incomeLevel', 'rob'] (QB-073). */
  subgroupDimensions: string[];
  /** Default 'separate_tau2' (QB-075). */
  subgroupTest: SubgroupTest;
  /** Default [] (QB-074). */
  metaRegressionCovariates: string[];
  /** Default {0.05, 0.1, 0.2, null, 'D2'}; null means no TSA is pre-specified (QB-076). */
  tsa: TsaProtocol | null;
  /** Field path to rationale, for settings that depart from the default (QB-072). Keys from DEPARTURE_FIELDS. */
  rationales: Partial<Record<DepartureField, string>>;
}

/** The settings compared with their defaults, in the order departures are listed (F12 design, Algorithms: Departures). */
export const DEPARTURE_FIELDS = [
  'analysis.measure',
  'analysis.model',
  'analysis.fixedMethod',
  'analysis.tau2Method',
  'analysis.hartungKnapp',
  'analysis.level',
  'analysis.continuityCorrection',
  'subgroupTest',
  'tsa',
  'tsa.alpha',
  'tsa.beta',
  'tsa.relativeRiskReduction',
  'tsa.controlEventProportion',
  'tsa.heterogeneityAdjustment',
] as const;

export type DepartureField = (typeof DEPARTURE_FIELDS)[number];

export interface Departure {
  field: DepartureField;
  defaultValue: JsonValue;
  value: JsonValue;
  rationale: string;
}

/** Fixed reporting conventions recorded in every protocol (QB-077). */
export interface ProtocolConventions {
  predictionInterval: 't_k_minus_2';
  i2: ['tau2_based', 'q_based'];
  zeroCells: 'add_0.5_all_cells_exclude_double_zero_ratio';
}

/** The canonical, hashed record of everything pre-specified (QB-082). Stored as canonicalJson in question.protocol_json. */
export interface ProtocolDocument {
  /** 2 from 14 Oct 2026 (QB-139): adds reviewType and studyDesigns, and outcomes of every type. */
  protocolVersion: 1 | 2;
  questionId: string;
  title: string;
  freeText: string;
  pico: Pico;
  /** designClass(studyDesigns) (EVM-172); always 'rct' in version 1. */
  studyDesign: QuestionStudyDesign;
  /** Version 2 only (QB-139); a version 1 document is an intervention review. */
  reviewType?: ReviewType;
  /** Version 2 only (QB-139); a version 1 document is of randomised controlled trials. */
  studyDesigns?: StudyDesign[];
  /** Copied unchanged and in stored order at freeze (QB-034). */
  eligibility: EligibilityRule[];
  /** IDs O1 (primary), O2, ... assigned at freeze (QB-047), each with its type and unit (QB-134; 'binary' only in version 1). */
  outcomes: OutcomeDefinition[];
  /** None is 'open' (QB-054). */
  clarifications: Clarification[];
  analysis: {
    options: AnalysisSettings;
    /** Always 'O1'. */
    primaryOutcomeId: string;
    subgroupDimensions: string[];
    subgroupTest: SubgroupTest;
    metaRegressionCovariates: string[];
    tsa: TsaProtocol | null;
    departures: Departure[];
  };
  conventions: ProtocolConventions;
  draftedBy: { provider: 'anthropic'; id: string } | null;
  /** Worker time at freeze, ISO 8601 UTC with milliseconds (QB-083). */
  frozenAt: string;
  /** 'user:<id>' of the owner who froze it (QB-083). */
  frozenBy: string;
}

/** POST PROTOCOL_ROUTES.questionFreeze (QB-080). Exactly these two keys. */
export interface FreezeProtocolRequest {
  confirm: 'freeze';
  expectedUpdatedAt: string;
}

/** POST PROTOCOL_ROUTES.questionEvidencePage (QB-110). */
export interface CreateEvidencePageFromQuestionResponse {
  slug: string;
}

/** QB-094: 'hash_mismatch' when the stored protocol no longer hashes to the stored hash. Always 'ok' for drafts. */
export type QuestionIntegrity = 'ok' | 'hash_mismatch';

/** details.reason of a 409 conflict from the question endpoints (F12 design, Endpoints). */
export type ProtocolConflictReason = 'clarifications_open' | 'rationale_missing' | 'stale_update' | 'not_frozen' | 'page_exists';

/** Added to Question (QB-062, QB-086, QB-092, QB-094). */
export interface QuestionProtocolFields {
  clarifications: Clarification[];
  protocolSettings: ProtocolSettings;
  /** The parsed ProtocolDocument, or null for a draft. */
  protocol: ProtocolDocument | null;
  protocolHash: string | null;
  frozenAt: string | null;
  evidencePageSlug: string | null;
  derivedFromQuestionId: string | null;
  /**
   * The model_call the content was drafted from, or null. A save quoting this same id keeps the
   * attribution without the 24-hour check that applies when a call is first attached (QB-062, QB-068).
   */
  draftModelCallId: string | null;
  integrity: QuestionIntegrity;
}

/** Added to SaveQuestionRequest (QB-052, QB-062, QB-065, QB-070). */
export interface SaveQuestionProtocolFields {
  /** [] when none. */
  clarifications: Clarification[];
  /** Omitted: defaults on create, unchanged on update. */
  protocolSettings?: ProtocolSettings;
  /** The model_call id of the draft the content came from, or null (replaces draftedBy, QB-062). */
  draftModelCallId: string | null;
  /** PUT only, and required there: the updatedAt the client last read (QB-065). */
  expectedUpdatedAt?: string;
}

/** Added to PicoDraftResponse (QB-062; AI-055). */
export interface PicoDraftProtocolFields {
  /** model_call id of the successful attempt, quoted back as SaveQuestionRequest.draftModelCallId. */
  modelCallId: string;
}
